"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Briefcase, Award, HeartHandshake, LifeBuoy } from "lucide-react";
import { Section } from "@/components/ui/Section";
import Image from "next/image";

const features = [
  { key: "experience", icon: Briefcase },
  { key: "approach", icon: Award },
  { key: "satisfaction", icon: HeartHandshake },
  { key: "support", icon: LifeBuoy },
];

export function About() {
  const t = useTranslations("Index");
  const tAbout = useTranslations("About");

  return (
    <Section
      id="about"
      title={t("about_title")}
      subtitle={t("about_subtitle")}
      className="bg-(--bg-primary) overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start pt-12">
        {/* LEFT COLUMN: Visual Anchor & Profile */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, x: -20, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="sticky top-32"
          >
            <div className="relative aspect-3/4 w-full rounded-none md:rounded-2xl overflow-hidden bg-(--bg-card) shadow-2xl shadow-black/20 group">
              {/* Image Pattern / Fallback */}
              <div className="absolute inset-0 bg-(--bg-card) z-0" />

              {/* Actual Image */}
              <div className="absolute inset-0 grayscale group-hover:grayscale-0 transition-all duration-700 ease-out scale-105 group-hover:scale-100 mix-blend-normal">
                <Image
                  src="/images/about.png"
                  alt="DevDJ Profile"
                  fill
                  className="object-cover object-center opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                />
              </div>

              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-linear-to-t from-(--bg-primary) via-transparent to-transparent opacity-80 z-10" />

              {/* Bottom Info - WebMaster & Developer */}
              <div className="absolute bottom-8 left-8 right-8 z-20">
                <div className="overflow-hidden">
                  <motion.h3
                    initial={{ y: "100%" }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="text-3xl font-bold font-outfit text-(--text-primary) mb-2"
                  >
                    {tAbout("profile_name")}
                  </motion.h3>
                </div>
                <div className="overflow-hidden">
                  <motion.p
                    initial={{ y: "100%" }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="text-(--text-secondary) font-mono text-sm tracking-widest uppercase"
                  >
                    {tAbout("profile_title")}
                  </motion.p>
                </div>
              </div>
            </div>

            {/* Decorative "VISION" Text - Top-Down Effect (Moved to Left) */}
            <div className="absolute -left-12 md:-left-24 top-0 hidden xl:flex flex-col items-center justify-center h-full z-0 pointer-events-none mix-blend-overlay opacity-40 select-none">
              <span className="text-[120px] font-black font-outfit text-(--text-tertiary) [writing-mode:vertical-rl] leading-none tracking-widest uppercase rotate-180">
                {tAbout("brand_text")}
              </span>
            </div>

            {/* Subtle Abstract Accent behind */}
            <div className="absolute -z-10 -bottom-12 -right-12 w-32 h-32 bg-(--brand-primary)/10 blur-3xl rounded-full" />
          </motion.div>
        </div>

        {/* RIGHT COLUMN: Narrative & Content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Lead Narrative */}
            <div className="prose prose-lg prose-invert max-w-none mb-20">
              <h3 className="text-4xl md:text-5xl font-bold font-outfit text-(--text-primary) leading-[1.1] mb-8">
                {tAbout("headline.prefix")}{" "}
                <span className="bg-linear-to-r from-[#ff0000] via-[#d60610] to-[#c80913] bg-clip-text text-transparent">
                  {tAbout("headline.highlight")}
                </span>{" "}
                {tAbout("headline.suffix")}
              </h3>
              <p className="text-xl text-(--text-secondary) font-light leading-relaxed">
                {tAbout("main_desc")}
              </p>
            </div>

            {/* Editorial Separator */}
            <div className="h-px flex-1 bg-(--border-subtle) mb-16" />

            {/* Principles Grid - Premium Card Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((item, index) => {
                const title = tAbout(`features.${item.key}.title`);
                const desc = tAbout(`features.${item.key}.desc`);

                return (
                  <div
                    key={item.key}
                    className="group flex flex-col items-start p-6 rounded-xl bg-(--bg-card) border border-(--border-subtle) hover:border-(--text-primary)/10 transition-all duration-500 hover:shadow-lg hover:shadow-black/5"
                  >
                    {/* Header: Icon & Index */}
                    <div className="flex items-center justify-between w-full mb-6">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-(--text-primary)/5 border border-transparent group-hover:border-(--text-primary)/10 text-(--text-secondary) group-hover:text-(--text-primary) transition-all duration-300">
                        <item.icon className="w-6 h-6" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono text-xs text-(--text-tertiary) uppercase tracking-widest opacity-50 group-hover:opacity-80 transition-opacity">
                        0{index + 1}
                      </span>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold font-outfit text-(--text-primary) mb-3 group-hover:text-(--text-primary) transition-colors">
                        {title}
                      </h4>
                      <p className="text-(--text-secondary) text-sm leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                        {desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
